import { useEffect, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { ChevronLeft } from "@acto/web-common/components";
import { useBackNavigation } from "@acto/taskpane/field-sales-2/navigation";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";

const Leading = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  minWidth: 0,
}));

const TitlePill = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 0,
  height: 48,
  maxWidth: 200,
  padding: "0 20px 0 20px",
  borderRadius: 99,
  border: `1.25px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  background: theme.fieldSalesTwo.colors.surface.glassMedium,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  justifyContent: "center",
  overflow: "hidden",
  opacity: 0,
  transform: "translateX(-8px)",
  transition: "opacity 0.2s ease, transform 0.2s ease",
  '&[data-visible="true"]': {
    opacity: 1,
    transform: "translateX(0)",
  },
}));

const Primary = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Secondary = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1,
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

interface UseDetailTopBarOptions {
  title?: string;
  subtitle?: string;
  backFallbackUrl: string;
}

/**
 * Back pill for a page one level below the customer shell.
 *
 * @returns `sentinelRef` for the page's own heading (the title pill fades in once it scrolls out
 * of view) and `topBar`, which the page renders to publish the pill into the shell's top bar.
 */
export function useDetailTopBar({ title, subtitle, backFallbackUrl }: UseDetailTopBarOptions) {
  const [showTitlePill, setShowTitlePill] = useState(false);
  const sentinelRef = useRef<HTMLHeadingElement>(null);
  const t = useTranslations("fieldSalesTwo");

  const handleBack = useBackNavigation(backFallbackUrl);
  const handleBackRef = useRef(handleBack);
  handleBackRef.current = handleBack;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowTitlePill(!entry.isIntersecting);
      },
      { rootMargin: "-120px 0px 0px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [title, subtitle]);

  const topBar = (
    <ShellSlotContent slot="topBarLeading" id="detailTopBar">
      <Leading>
        <HeaderIconButton onClick={() => handleBackRef.current()} aria-label={t("back", { defaultValue: "Back" })}>
          <ChevronLeft width={8} height={14} />
        </HeaderIconButton>
        {title && (
          <TitlePill data-visible={showTitlePill}>
            <Primary>{title}</Primary>
            {subtitle && <Secondary>{subtitle}</Secondary>}
          </TitlePill>
        )}
      </Leading>
    </ShellSlotContent>
  );

  return { sentinelRef, topBar };
}
